/**
 * Context Binding Engine — caller 2 (SED-send): typed-claims prompt renderer.
 *
 * `buildSiteContextBlock`'s [SITE CONTEXT — TYPED CLAIMS] body routes
 * through here so the LLM reads the SAME unit-normalised, range-faithful,
 * provenanced values the CBE fills into intent / financial / SED fields —
 * instead of the raw `claimedValue` strings the legacy line carried.
 *
 * Contract (hard guarantees, pinned by tests):
 *   - claims-in === entries-out. EVERY already-filtered claim renders an
 *     entry. A claim the engine cannot type (unmapped tag — e.g. 'general',
 *     'doc-reference', 'prior-study', 'unverifiable' — or every resolution
 *     skipReason'd) FALLS THROUGH to the legacy line format unchanged. The
 *     resolver silently dropping unmapped tags is BY DESIGN for field fills;
 *     the prompt renderer must never drop a user-declared claim.
 *   - Ranges render as `min–max unit` (open ends `≥n` / `≤n`) — NEVER a
 *     midpoint (CLAUDE.md §2 / CBE no-fabrication guard 3).
 *   - One claim fanning out to several targets renders ONE entry: the first
 *     line carries the claim key + confidence/source suffix; each further
 *     DISTINCT rendered value gets a `↳` continuation line. Targets sharing
 *     an identical rendered value are deduped onto one line.
 *   - The `span:` excerpt line is emitted ONLY for `document-extracted`
 *     claims — prose-extracted spans already ship verbatim in
 *     [SITE CONTEXT — PROSE].
 *   - Cross-claim same-field divergences are SURFACED (`• CONTEXT:` lines
 *     for the ⚠ CONFLICTS section), never silently picked
 *     (precedence-resolution directive).
 *
 * Join mechanics: `CbeResolution` carries no claim id by design, so the
 * renderer invokes the engine PER CLAIM (`claimsToFacts([claim])`) — the
 * invocation itself is the claim↔resolution join.
 *
 * Pure / synchronous. No I/O.
 */
import { filledResolutions, resolveContextBindings } from './resolveContextBindings';
import { mergeResolutions } from './mergeResolutions';
import { claimsToFacts } from '../../lib/contextFinancialFill';
import { spanExcerpt } from './spanExcerpt';
import { BILLION, MILLION } from './coercions';
import type { CbeResolution, CbeValue } from './cbeTypes';
import type { ExtractedClaim } from '../../types/siteContext';
import type { ReportTypeId } from '../../config/reportTypes';

/** One rendered claim entry — `lines` has ≥1 line. */
export interface RenderedClaimEntry {
  /** The claim this entry renders (input order preserved). */
  readonly claimId: string;
  /** 'typed' ⇒ ≥1 CBE-filled resolution rendered; 'legacy' ⇒ fallthrough. */
  readonly kind: 'typed' | 'legacy';
  readonly lines: readonly string[];
}

export interface RenderedClaimContext {
  /** One entry per input claim — invariant: entries.length === claims.length. */
  readonly entries: readonly RenderedClaimEntry[];
  /** Flattened prompt lines for the [SITE CONTEXT — TYPED CLAIMS] body. */
  readonly lines: readonly string[];
  /** `• CONTEXT:` divergence lines for the ⚠ CONFLICTS section. */
  readonly divergenceLines: readonly string[];
}

/**
 * Prompt-only budget metadata. The source `SiteContext.claims` array remains
 * complete on the site/report snapshot; this projection only decides which
 * claim lines fit in a single report-generation prompt.
 */
export interface BudgetedRenderedClaimContext extends RenderedClaimContext {
  /** Empty for normal-size input so the historical prompt is byte-identical. */
  readonly budgetMetadataLines: readonly string[];
  readonly totalClaimCount: number;
  readonly includedClaimCount: number;
  readonly omittedClaimCount: number;
  readonly compactedClaimCount: number;
  readonly omittedDivergenceCount: number;
  readonly inputChars: number;
  readonly budgetChars: number;
}

export interface ClaimPromptBudgetOptions {
  readonly reportType?: ReportTypeId;
  /** Total chars across metadata, claim lines, and divergence lines. */
  readonly maxChars?: number;
  /** Maximum chars for any one claim entry before explicit compaction. */
  readonly perClaimMaxChars?: number;
  /** Claims with live SED conflicts remain high priority across report types. */
  readonly priorityClaimIds?: ReadonlySet<string>;
}

export const CLAIM_PROMPT_TOTAL_BUDGET_CHARS = 8_000;
export const CLAIM_PROMPT_PER_CLAIM_BUDGET_CHARS = 1_200;

/* ── Value rendering ──────────────────────────────────────────────────── */

/** Strip trailing fraction zeros from a toFixed result ("3.00" → "3"). */
function trimZeros(s: string): string {
  return s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s;
}

/**
 * Deterministic plain-number display. Rounds float noise from unit
 * conversion (ha→acres ×2.47105) at 1e-3 — DISPLAY rounding only, the
 * filled field value is untouched.
 */
function plainNum(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  return String(Math.round(n * 1000) / 1000);
}

/** Currency magnitude keeping the source's stated scale ($3.0e9 → "$3B"). */
function currencyNum(n: number): string {
  if (!Number.isFinite(n)) return `$${String(n)}`;
  const abs = Math.abs(n);
  if (abs >= BILLION) return `$${trimZeros((n / BILLION).toFixed(2))}B`;
  if (abs >= MILLION) return `$${trimZeros((n / MILLION).toFixed(2))}M`;
  return `$${plainNum(n)}`;
}

/** A number with its canonical unit ($-prefix, %-tight, else suffixed). */
function numWithUnit(n: number, unit: string | undefined): string {
  if (unit === '$') return currencyNum(n);
  if (unit && unit.startsWith('$/')) return `${currencyNum(n)}/${unit.slice(2)}`;
  if (unit === '%') return `${plainNum(n)}%`;
  return unit ? `${plainNum(n)} ${unit}` : plainNum(n);
}

/**
 * Render a CbeValue for the prompt. Ranges keep `min–max` (open ends
 * `≥n` / `≤n`, NEVER a midpoint); dates carry ISO + stated precision;
 * multis show `count × perUnit = total unit`; an 'approximate' qualifier
 * renders a `~` prefix without fabricating a band.
 */
export function renderCbeValue(v: CbeValue): string {
  const approx = v.qualifier === 'approximate' ? '~' : '';
  switch (v.kind) {
    case 'scalar':
      return typeof v.value === 'number'
        ? `${approx}${numWithUnit(v.value, v.unit)}`
        : `"${v.value}"`;
    case 'range': {
      if (v.min !== null && v.max !== null) {
        // $-denominated endpoints render prefix-style each (scales may
        // differ: $900M–$1.2B); plain units share one trailing suffix.
        if (v.unit === '$' || v.unit?.startsWith('$/')) {
          return `${approx}${numWithUnit(v.min, v.unit)}–${numWithUnit(v.max, v.unit)}`;
        }
        const suffix = v.unit === '%' ? '%' : v.unit ? ` ${v.unit}` : '';
        return `${approx}${plainNum(v.min)}–${plainNum(v.max)}${suffix}`;
      }
      if (v.min !== null) return `≥${numWithUnit(v.min, v.unit)}`;
      if (v.max !== null) return `≤${numWithUnit(v.max, v.unit)}`;
      return '(empty range)'; // unreachable — toRange never emits null/null
    }
    case 'date':
      return `${v.iso} (${v.precision} precision)`;
    case 'bool':
      return String(v.value);
    case 'enum':
      return v.value;
    case 'complex':
      return `"${v.value}"`;
    case 'multi':
      return `${plainNum(v.count)} × ${plainNum(v.perUnit)} = ${plainNum(v.total)}${v.unit ? ` ${v.unit}` : ''}`;
    default: {
      const _exhaustive: never = v;
      return _exhaustive;
    }
  }
}

/* ── Line rendering ───────────────────────────────────────────────────── */

/** The `({conf}%, {source[:docId]}{, asserted date})` suffix — byte-identical
 *  to the legacy buildSiteContextBlock rendering. */
function confSourceSuffix(c: ExtractedClaim): string {
  const confPct = `${Math.round(c.extractorConfidence ?? 0)}%`;
  const source = c.source ?? 'user-typed';
  const sourceTag = c.sourceDocId ? `${source}:${c.sourceDocId}` : source;
  const asserted = c.lastWrittenAt ? `, asserted ${c.lastWrittenAt.slice(0, 10)}` : '';
  return `${confPct}, ${sourceTag}${asserted}`;
}

/** The legacy `  {category}{.field} = {value} ({suffix})` line — UNCHANGED
 *  fallthrough format for claims the engine cannot type. */
function legacyClaimLine(c: ExtractedClaim): string {
  const fieldSuffix = c.field ? `.${c.field}` : '';
  const valueStr = c.claimedValue !== undefined && c.claimedValue !== null && c.claimedValue !== ''
    ? typeof c.claimedValue === 'string' ? `"${c.claimedValue}"` : String(c.claimedValue)
    : `"${c.claim}"`;
  return `  ${c.category}${fieldSuffix} = ${valueStr} (${confSourceSuffix(c)})`;
}

/** Typed entry lines: first line carries the claim key + suffix; each
 *  further DISTINCT rendered value gets a `↳` continuation. Targets with
 *  identical rendered values dedupe onto one line. */
function typedClaimLines(c: ExtractedClaim, filled: readonly CbeResolution[]): string[] {
  const byValue = new Map<string, string[]>();
  for (const r of filled) {
    if (r.value === undefined) continue; // filledResolutions guarantees, but guard
    const rendered = renderCbeValue(r.value);
    const target = `${r.targetField.surface}:${r.targetField.path}`;
    const targets = byValue.get(rendered) ?? [];
    if (!targets.includes(target)) targets.push(target);
    byValue.set(rendered, targets);
  }
  const fieldSuffix = c.field ? `.${c.field}` : '';
  const lines: string[] = [];
  for (const [rendered, targets] of byValue) {
    if (lines.length === 0) {
      lines.push(`  ${c.category}${fieldSuffix} = ${rendered} (${confSourceSuffix(c)}) → ${targets.join(', ')}`);
    } else {
      lines.push(`    ↳ ${rendered} → ${targets.join(', ')}`);
    }
  }
  return lines;
}

/* ── Entry point ──────────────────────────────────────────────────────── */

/**
 * Render the ALREADY-FILTERED claims (caller applies the SCE-3 / IIA-3
 * filters) into typed-claims prompt lines + cross-claim divergence lines.
 */
export function renderClaimContextLines(claims: readonly ExtractedClaim[]): RenderedClaimContext {
  const entries: RenderedClaimEntry[] = [];
  const filledAll: CbeResolution[] = [];

  for (const c of claims) {
    // Per-claim invocation IS the claim↔resolution join (no fact id on
    // CbeResolution by design — do not add one).
    const filled = filledResolutions(resolveContextBindings(claimsToFacts([c])));
    filledAll.push(...filled);

    const lines = filled.length > 0 ? typedClaimLines(c, filled) : [legacyClaimLine(c)];
    // Doc-extracted claims carry a verbatim span excerpt so the model can
    // cross-check the typed value against its source document. Prose spans
    // already appear in [SITE CONTEXT — PROSE].
    if (c.source === 'document-extracted' && c.sourceSpan && c.sourceSpan.trim() !== '') {
      lines.push(`      span: "${spanExcerpt(c.sourceSpan)}"`);
    }
    entries.push({ claimId: c.id, kind: filled.length > 0 ? 'typed' : 'legacy', lines });
  }

  // Cross-claim same-field divergence: merge ALL filled resolutions with an
  // empty field-state map — every surviving conflict is two CONTEXT claims
  // resolving different values onto the same target. Surfaced, never
  // silently picked (precedence-resolution directive).
  const merged = mergeResolutions(filledAll, new Map());
  const divergenceLines = merged.conflicts
    .filter(cf => cf.reason === 'context-context-divergence')
    .map(cf => `  • CONTEXT: ${cf.label} — ${renderUnknownValue(cf.existingValue)} vs ${renderCbeValue(cf.contextValue)}`);

  return { entries, lines: entries.flatMap(e => [...e.lines]), divergenceLines };
}

/* ── Prompt budget projection ─────────────────────────────────────────── */

/**
 * Category affinity is deliberately coarse. It is only a tie-breaker when a
 * site has more claims than the prompt budget can carry; it never suppresses
 * a claim on a normal-size site and it never changes stored evidence.
 */
function reportCategoryAffinity(reportType: ReportTypeId | undefined, category: ExtractedClaim['category']): number {
  if (!reportType || reportType === 'feasibility' || reportType === 'executive-summary' || reportType === 'custom-brief') {
    return 1;
  }
  const starts = (prefix: string): boolean => category === prefix || category.startsWith(`${prefix}.`);
  switch (reportType) {
    case 'environmental':
      return starts('environmental') || starts('regulatory') || starts('geotechnical') || starts('water') ? 2 : 0;
    case 'test-fit':
      return starts('parcel') || starts('geotechnical') || starts('technology') || starts('water')
        || category === 'power.btm' || category === 'environmental.wetlands' ? 2 : 0;
    case 'power-availability':
      return starts('power') || starts('schedule') || starts('technology') || category === 'finance.ppa'
        || category === 'commitment' ? 2 : 0;
    case 'water-resource':
      return starts('water') || category === 'technology.cooling' || starts('environmental')
        || category === 'regulatory.permits' || starts('parcel') ? 2 : 0;
    case 'interconnection':
      return starts('connectivity') || category === 'power.grid' || category === 'power.timeline'
        || starts('schedule') || category === 'regulatory.permits' ? 2 : 0;
    case 'supply-chain-risk':
      return starts('technology') || starts('schedule') || category === 'finance.cost'
        || category === 'commitment' || category === 'prior-study' || category === 'doc-reference'
        || category === 'power.btm' ? 2 : 0;
    case 'financial':
      return starts('finance') || starts('power') || starts('schedule') || starts('technology')
        || category === 'commitment' ? 2 : 0;
    case 'public-sentiment':
      return starts('environmental') || starts('water') || starts('regulatory') || starts('parcel')
        || category === 'general' || category === 'commitment' ? 2 : 0;
    default: {
      const _exhaustive: never = reportType;
      return _exhaustive;
    }
  }
}

function isoRecency(c: ExtractedClaim): string {
  const syncDate = c.syncHistory?.reduce(
    (latest, h) => h.resolvedAt > latest ? h.resolvedAt : latest,
    '',
  ) ?? '';
  return c.lastWrittenAt && c.lastWrittenAt > syncDate ? c.lastWrittenAt : syncDate;
}

/**
 * Deterministic priority using only signals already present on a claim:
 * report-category affinity, live conflict, direct/authored provenance,
 * current write/sync time, quality warnings, and extractor confidence.
 */
function compareClaimPriority(
  a: { claim: ExtractedClaim; index: number },
  b: { claim: ExtractedClaim; index: number },
  options: ClaimPromptBudgetOptions,
): number {
  const affinityDelta = reportCategoryAffinity(options.reportType, b.claim.category)
    - reportCategoryAffinity(options.reportType, a.claim.category);
  if (affinityDelta !== 0) return affinityDelta;

  const conflictDelta = Number(options.priorityClaimIds?.has(b.claim.id) ?? false)
    - Number(options.priorityClaimIds?.has(a.claim.id) ?? false);
  if (conflictDelta !== 0) return conflictDelta;

  const authority = (c: ExtractedClaim): number => {
    if (c.state === 'authored' || c.authoredKind === 'structured') return 3;
    if ((c.source ?? 'user-typed') === 'user-typed') return 2;
    if (c.source === 'merged') return 1;
    return 0;
  };
  const authorityDelta = authority(b.claim) - authority(a.claim);
  if (authorityDelta !== 0) return authorityDelta;

  const recencyDelta = isoRecency(b.claim).localeCompare(isoRecency(a.claim));
  if (recencyDelta !== 0) return recencyDelta;

  const qualityDelta = Number((a.claim.qualityWarnings?.length ?? 0) > 0)
    - Number((b.claim.qualityWarnings?.length ?? 0) > 0);
  if (qualityDelta !== 0) return qualityDelta;

  const confidenceDelta = (b.claim.extractorConfidence ?? 0) - (a.claim.extractorConfidence ?? 0);
  if (confidenceDelta !== 0) return confidenceDelta;
  return a.index - b.index;
}

function fnv1a(value: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/** Keep normal identities verbatim; pathological identities retain a stable digest. */
function boundedIdentity(value: string | undefined, maxChars: number): string {
  const safe = value ?? '';
  if (safe.length <= maxChars) return safe;
  return `${safe.slice(0, Math.max(1, maxChars - 12))}…#${fnv1a(safe)}`;
}

function compactTextWithMarker(value: string, maxChars: number): string {
  if (value.length <= maxChars) return value;
  let keepChars = Math.max(1, maxChars - 24);
  let marker = '';
  // The marker length depends on the omitted-count digit width. Recompute to
  // a fixed point so the printed count is the exact number of removed chars.
  for (let i = 0; i < 3; i++) {
    marker = `…[${value.length - keepChars} chars omitted]`;
    keepChars = Math.max(1, maxChars - marker.length);
  }
  marker = `…[${value.length - keepChars} chars omitted]`;
  return `${value.slice(0, keepChars)}${marker}`;
}

function compactClaimEntry(
  claim: ExtractedClaim,
  entry: RenderedClaimEntry,
  perClaimMaxChars: number,
): RenderedClaimEntry {
  const originalChars = entry.lines.join('\n').length;
  if (originalChars <= perClaimMaxChars) return entry;

  const field = claim.field ? `.${boundedIdentity(claim.field, 120)}` : '';
  const key = `${claim.category}${field}`;
  const confPct = `${Math.round(claim.extractorConfidence ?? 0)}%`;
  const source = claim.source ?? 'user-typed';
  const sourceTag = claim.sourceDocId
    ? `${source}:${boundedIdentity(claim.sourceDocId, 180)}`
    : source;
  const asserted = claim.lastWrittenAt ? `, asserted ${claim.lastWrittenAt.slice(0, 10)}` : '';
  const targetText = boundedIdentity(
    entry.lines
      .flatMap(line => {
        const arrow = line.indexOf('→');
        return arrow >= 0 ? [line.slice(arrow + 1).trim()] : [];
      })
      .join(', '),
    320,
  );
  const targetSuffix = targetText ? ` → ${targetText}` : '';
  const marker = ` [claimId=${boundedIdentity(claim.id, 120)}; compactedFrom=${originalChars} chars]`;
  const suffix = ` (${confPct}, ${sourceTag}${asserted})${targetSuffix}${marker}`;
  const prefix = `  ${key} = `;
  // Prefer the renderer's canonical first value (unit-normalised, range-
  // faithful) over reconstructing from raw claimedValue. This keeps CBE
  // semantics intact even when a typed entry fans out to enough targets to
  // require compaction.
  const originalField = claim.field ? `.${claim.field}` : '';
  const originalPrefix = `  ${claim.category}${originalField} = `;
  const sourceSuffixStart = entry.lines[0]?.lastIndexOf(` (${confPct}, `) ?? -1;
  const canonicalValue = entry.lines[0]?.startsWith(originalPrefix) && sourceSuffixStart >= originalPrefix.length
    ? entry.lines[0].slice(originalPrefix.length, sourceSuffixStart)
    : undefined;
  const raw = claim.claimedValue !== undefined && claim.claimedValue !== null && claim.claimedValue !== ''
    ? claim.claimedValue
    : claim.claim;
  const serialized = canonicalValue
    ?? (typeof raw === 'string' ? JSON.stringify(raw) : String(raw));
  const valueBudget = Math.max(48, perClaimMaxChars - prefix.length - suffix.length);
  const line = `${prefix}${compactTextWithMarker(serialized, valueBudget)}${suffix}`;
  return { claimId: claim.id, kind: entry.kind, lines: [line] };
}

function contextChars(
  metadataLines: readonly string[],
  entries: readonly RenderedClaimEntry[],
  divergenceLines: readonly string[],
): number {
  return [...metadataLines, ...entries.flatMap(e => [...e.lines]), ...divergenceLines].join('\n').length;
}

function buildBudgetMetadata(
  options: Required<Pick<ClaimPromptBudgetOptions, 'maxChars' | 'perClaimMaxChars'>> & Pick<ClaimPromptBudgetOptions, 'reportType'>,
  total: number,
  included: number,
  compacted: number,
  omittedDivergences: number,
  inputChars: number,
): string[] {
  return [
    `[CLAIM PROMPT BUDGET: reportType=${options.reportType ?? 'unspecified'}; total=${total}; included=${included}; omitted=${total - included}; compacted=${compacted}; divergenceLinesOmitted=${omittedDivergences}; inputChars=${inputChars}; budgetChars=${options.maxChars}; perClaimChars=${options.perClaimMaxChars}]`,
    'Omitted or compacted claim content remains in the frozen siteContextSnapshot; it was not supplied to this prompt and was not deleted from evidence.',
  ];
}

/**
 * Render claims with fail-visible, evidence-preserving prompt discipline.
 * Normal-size input returns byte-identical lines and no metadata. Oversized
 * input is ranked deterministically, each included entry is bounded, and the
 * exact omission/compaction counts are printed into the prompt.
 */
export function renderBudgetedClaimContextLines(
  claims: readonly ExtractedClaim[],
  options: ClaimPromptBudgetOptions = {},
): BudgetedRenderedClaimContext {
  const maxChars = Math.max(512, options.maxChars ?? CLAIM_PROMPT_TOTAL_BUDGET_CHARS);
  const perClaimMaxChars = Math.max(256, Math.min(maxChars, options.perClaimMaxChars ?? CLAIM_PROMPT_PER_CLAIM_BUDGET_CHARS));
  const full = renderClaimContextLines(claims);
  const inputChars = [...full.lines, ...full.divergenceLines].join('\n').length;
  const fullEntriesFit = full.entries.every(e => e.lines.join('\n').length <= perClaimMaxChars);
  if (inputChars <= maxChars && fullEntriesFit) {
    return {
      ...full,
      budgetMetadataLines: [],
      totalClaimCount: claims.length,
      includedClaimCount: claims.length,
      omittedClaimCount: 0,
      compactedClaimCount: 0,
      omittedDivergenceCount: 0,
      inputChars,
      budgetChars: maxChars,
    };
  }

  const requiredOptions = { ...options, maxChars, perClaimMaxChars };
  const ranked = claims
    .map((claim, index) => ({ claim, index, entry: compactClaimEntry(claim, full.entries[index]!, perClaimMaxChars) }))
    .sort((a, b) => compareClaimPriority(a, b, requiredOptions));

  // Leave enough room for explicit metadata before greedily filling entries.
  // The exact fit loop below accounts for divergence lines and variable
  // metadata length; this reserve only prevents avoidable add/remove churn.
  const metadataReserve = Math.min(640, Math.floor(maxChars * 0.2));
  const selected: typeof ranked = [];
  let selectedChars = 0;
  for (const candidate of ranked) {
    const chars = candidate.entry.lines.join('\n').length + (selected.length > 0 ? 1 : 0);
    if (selectedChars + chars > maxChars - metadataReserve) continue;
    selected.push(candidate);
    selectedChars += chars;
  }

  const buildResult = (): {
    entries: RenderedClaimEntry[];
    divergenceLines: string[];
    metadataLines: string[];
    omittedDivergenceCount: number;
    compactedCount: number;
  } => {
    const selectedClaims = selected.map(x => x.claim);
    const selectedRendered = renderClaimContextLines(selectedClaims);
    const entries = selected.map(x => x.entry);
    const compactedCount = entries.filter((entry, i) => entry.lines.join('\n') !== selectedRendered.entries[i]?.lines.join('\n')).length;
    const boundedDivergences = selectedRendered.divergenceLines.map(line => compactTextWithMarker(line, 600));
    const divergenceLines: string[] = [];
    const preliminaryMetadata = buildBudgetMetadata(
      requiredOptions,
      claims.length,
      selected.length,
      compactedCount,
      0,
      inputChars,
    );
    for (const line of boundedDivergences) {
      if (contextChars(preliminaryMetadata, entries, [...divergenceLines, line]) <= maxChars) divergenceLines.push(line);
    }
    const omittedDivergenceCount = boundedDivergences.length - divergenceLines.length;
    const metadataLines = buildBudgetMetadata(
      requiredOptions,
      claims.length,
      selected.length,
      compactedCount,
      omittedDivergenceCount,
      inputChars,
    );
    return { entries, divergenceLines, metadataLines, omittedDivergenceCount, compactedCount };
  };

  let result = buildResult();
  // Metadata grows as counts gain digits and selected claims can create new
  // divergence lines. Remove the lowest-priority selected claim until the
  // complete projection — including omission metadata — fits exactly.
  while (selected.length > 0 && contextChars(result.metadataLines, result.entries, result.divergenceLines) > maxChars) {
    selected.pop();
    result = buildResult();
  }

  return {
    entries: result.entries,
    lines: result.entries.flatMap(e => [...e.lines]),
    divergenceLines: result.divergenceLines,
    budgetMetadataLines: result.metadataLines,
    totalClaimCount: claims.length,
    includedClaimCount: result.entries.length,
    omittedClaimCount: claims.length - result.entries.length,
    compactedClaimCount: result.compactedCount,
    omittedDivergenceCount: result.omittedDivergenceCount,
    inputChars,
    budgetChars: maxChars,
  };
}

/** existingValue on a context-context divergence is the winning CbeValue;
 *  guard so an unexpected primitive still renders legibly. */
function renderUnknownValue(v: unknown): string {
  if (typeof v === 'object' && v !== null && 'kind' in v && 'provenance' in v) {
    return renderCbeValue(v as CbeValue);
  }
  return String(v);
}
